import { useTranslation } from "react-i18next";

import { CloseButton, Combobox, Group, Input, Pill, PillsInput, ScrollArea, Text, useCombobox } from "@mantine/core";

import { useDeployments, useFilteredDeployments } from "@/api/resources/deployments/hooks";
import { useWorkspaces } from "@/api/resources/workspaces/hooks";
import SkeletonBars from "@/components/Skeleton/SkeletonBars";
import WorkspaceAbbreviationChip from "@/components/Workspace/WorkspaceAbbreviationChip";
import { useGlobalFilters } from "@/hooks/useGlobalFilters";
import { deploymentName } from "@/utils/format";
import Icon from "@/utils/icons";

const MAX_DISPLAYED_VALUES = 1;
const PILL_WIDTH_SMALL = 150;

export const DeploymentSelectorGlobal = () => {
  const { t } = useTranslation();
  const { options, setOptions } = useGlobalFilters();
  const { data: deployments, isLoading: loadingDeployments } = useDeployments();
  const { data: workspaces, isLoading: loadingWorkspaces } = useWorkspaces();
  const { data: filteredDeployments, isLoading: loadingFilteredDeployments } = useFilteredDeployments();

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });

  const valueLength = options.deployments.length;
  const valueIsSet = valueLength > 0;

  const handleValueSelect = (value: string) => {
    setOptions((prev) => ({ ...prev, deployments: [...options.deployments, value] }));
  };

  const handleValueRemove = (value: string) => {
    setOptions((prev) => ({ ...prev, deployments: options.deployments.filter((v) => v !== value) }));
  };

  const handleValueClear = () => {
    setOptions((prev) => ({ ...prev, deployments: [] }));
  };

  const PILL_WIDTH = combobox.dropdownOpened
    ? undefined
    : valueLength <= MAX_DISPLAYED_VALUES
      ? undefined
      : PILL_WIDTH_SMALL;

  const values = options.deployments
    .slice(
      0,
      combobox.dropdownOpened
        ? valueLength
        : MAX_DISPLAYED_VALUES === valueLength
          ? MAX_DISPLAYED_VALUES
          : MAX_DISPLAYED_VALUES - 1,
    )
    .map((deployment) => {
      const _deployment = deployments?.find((d) => d.id === deployment);
      const _workspace = workspaces?.find((w) => w.id === _deployment?.workspace_id);
      return (
        <Pill key={deployment} withRemoveButton onRemove={() => handleValueRemove(deployment)} w={PILL_WIDTH}>
          <Text size="xs" truncate="end" maw={PILL_WIDTH_SMALL}>
            {deploymentName(_deployment, _workspace)}
          </Text>
        </Pill>
      );
    });

  const _options = (filteredDeployments ?? [])
    .filter((fd) => !options.deployments.includes(fd.id))
    .map((deployment) => {
      const _workspace = workspaces?.find((w) => w.id === deployment.workspace_id);
      return (
        <Combobox.Option value={deployment.id} key={deployment.id} active={options.deployments.includes(deployment.id)}>
          <Group wrap="nowrap" gap={2}>
            {deployment.name}
            <WorkspaceAbbreviationChip {..._workspace} />
          </Group>
        </Combobox.Option>
      );
    });

  if (loadingDeployments || loadingWorkspaces || loadingFilteredDeployments) {
    return <SkeletonBars count={1} />;
  }

  // TODO: extract 24rem, possibly use at other selects as well
  return (
    <Combobox store={combobox} onOptionSubmit={handleValueSelect} withinPortal={true} width="24rem">
      <Combobox.DropdownTarget>
        <PillsInput
          leftSection={<Icon.FILTER />}
          rightSection={
            valueIsSet ? (
              <CloseButton size="sm" onMouseDown={(event) => event.preventDefault()} onClick={handleValueClear} />
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={valueIsSet ? "all" : "none"}
          pointer
          onClick={() => combobox.toggleDropdown()}
        >
          <Pill.Group>
            {valueIsSet ? (
              <>
                {values}
                {!combobox.dropdownOpened && valueLength > MAX_DISPLAYED_VALUES && (
                  <Pill>{t("deployments-select", { c: valueLength })}</Pill>
                )}
              </>
            ) : (
              <Input.Placeholder>{t("deployments")}</Input.Placeholder>
            )}

            <Combobox.EventsTarget>
              <PillsInput.Field type="hidden" onBlur={() => combobox.closeDropdown()} />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize type="scroll" mah="16rem">
            {_options.length > 0 ? _options : <Combobox.Empty>{t("empty.select-results")}</Combobox.Empty>}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default DeploymentSelectorGlobal;
